/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};
function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') {
    return;
  }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}
var css_248z = ".TableFlowImporter {\n  border: none;\n  background-color: transparent;\n  padding: 0 1rem;\n  border-radius: 1.2rem;\n  color: inherit;\n  cursor: pointer;\n  font-weight: 500;\n  font-size: 14px;\n  height: 2.4rem;\n  display: inline-flex;\n  gap: 0.5rem;\n  align-items: center;\n  transition: filter 0.2s ease-out;\n}\n\n.TableFlowImporter svg {\n  display: block;\n}\n\n.TableFlowImporter svg path {\n  stroke: currentColor !important;\n}\n\n.TableFlowImporter:hover,\n.TableFlowImporter:active {\n  filter: brightness(1.2);\n}\n\n.TableFlowImporter-dialog {\n  background-color: rgba(0, 0, 0, 0.1);\n  border-radius: 1rem;\n  border: none;\n  position: fixed;\n  inset: 0;\n  padding: 0;\n}\n\n.TableFlowImporter-dialog::backdrop {\n  background-color: rgba(0, 0, 0, 0.85);\n}\n\n.TableFlowImporter-dialog iframe {\n  border-radius: 1rem;\n  width: 80vw;\n  height: 80vh;\n  min-width: 907px;\n  min-height: 650px;\n  border: none;\n}\n";
styleInject(css_248z);
var postMessages = [];
function createTableFlowImporter(_a) {
  var _b = _a.elementId,
    elementId = _b === void 0 ? "tableflow-importer" : _b,
    _c = _a.onRequestClose,
    onRequestClose = _c === void 0 ? function () {
      return null;
    } : _c,
    importerId = _a.importerId,
    template = _a.template,
    hostUrl = _a.hostUrl,
    _d = _a.darkMode,
    darkMode = _d === void 0 ? false : _d,
    _e = _a.primaryColor,
    primaryColor = _e === void 0 ? "#7a5ef8" : _e,
    _f = _a.metadata,
    metadata = _f === void 0 ? "{}" : _f,
    closeOnClickOutside = _a.closeOnClickOutside,
    onComplete = _a.onComplete,
    customStyles = _a.customStyles,
    className = _a.className,
    showImportLoadingStatus = _a.showImportLoadingStatus,
    showDownloadTemplateButton = _a.showDownloadTemplateButton,
    skipHeaderRowSelection = _a.skipHeaderRowSelection,
    schemaless = _a.schemaless;
  // CSS classes
  var baseClass = "TableFlowImporter";
  var themeClass = darkMode && "".concat(baseClass, "-dark");
  var dialogClass = ["".concat(baseClass, "-dialog"), themeClass, className].filter(function (i) {
    return i;
  }).join(" ");
  // dialog element
  var dialog = document.getElementById(elementId);
  var backdropClick = function () {
    if (closeOnClickOutside) onRequestClose();
  };
  if (dialog === null) {
    dialog = document.createElement("dialog");
    document.body.appendChild(dialog);
    dialog.setAttribute("id", elementId);
    dialog.addEventListener("click", backdropClick);
  }
  dialog.setAttribute("class", dialogClass);
  // iframe element
  var urlParams = {
    importerId: importerId,
    template: parseObjectOrStringJSON("template", template),
    darkMode: darkMode.toString(),
    primaryColor: primaryColor,
    metadata: parseObjectOrStringJSON("metadata", metadata),
    isOpen: "true",
    onComplete: onComplete ? "true" : "false",
    customStyles: JSON.stringify(customStyles),
    showImportLoadingStatus: parseOptionalBoolean(showImportLoadingStatus),
    showDownloadTemplateButton: parseOptionalBoolean(showDownloadTemplateButton),
    skipHeaderRowSelection: parseOptionalBoolean(skipHeaderRowSelection),
    schemaless: parseOptionalBoolean(schemaless)
  };
  var uploaderUrl = getUploaderUrl(urlParams, hostUrl);
  function messageListener(e) {
    if (!e || !(e === null || e === void 0 ? void 0 : e.data)) return;
    var messageData = e.data;
    if ((messageData === null || messageData === void 0 ? void 0 : messageData.source) !== "tableflow-importer" || (messageData === null || messageData === void 0 ? void 0 : messageData.importerId) !== importerId || !(messageData === null || messageData === void 0 ? void 0 : messageData.id) || postMessages.includes(messageData.id)) {
      return;
    }
    if ((messageData === null || messageData === void 0 ? void 0 : messageData.type) === "start" && urlParams.isOpen !== "true") {
      urlParams = __assign(__assign({}, urlParams), {
        isOpen: "true"
      });
      var uploaderUrl_1 = getUploaderUrl(urlParams, hostUrl);
      dialog.innerHTML = "<iframe src=\"".concat(uploaderUrl_1, "\" />");
    }
    if ((messageData === null || messageData === void 0 ? void 0 : messageData.type) === "complete" && onComplete) {
      onComplete({
        data: (messageData === null || messageData === void 0 ? void 0 : messageData.data) || null,
        error: (messageData === null || messageData === void 0 ? void 0 : messageData.error) || null
      });
      postMessages.push(messageData === null || messageData === void 0 ? void 0 : messageData.id);
    }
    if ((messageData === null || messageData === void 0 ? void 0 : messageData.type) === "close" && onRequestClose) {
      onRequestClose();
      postMessages.push(messageData === null || messageData === void 0 ? void 0 : messageData.id);
      if (urlParams.isOpen !== "false") {
        urlParams = __assign(__assign({}, urlParams), {
          isOpen: "false"
        });
        var uploaderUrl_2 = getUploaderUrl(urlParams, hostUrl);
        dialog.innerHTML = "<iframe src=\"".concat(uploaderUrl_2, "\" />");
      }
    }
  }
  window.addEventListener("message", messageListener);
  dialog.innerHTML = "<iframe src=\"".concat(uploaderUrl, "\" />");
  return dialog;
}
function getUploaderUrl(urlParams, hostUrl) {
  var searchParams = new URLSearchParams(urlParams);
  var defaultImporterUrl = "https://importer.tableflow.com";
  return "".concat(hostUrl ? hostUrl : defaultImporterUrl, "?").concat(searchParams);
}
// Allows for the user to pass in JSON as either an object or a string
var parseObjectOrStringJSON = function (name, param) {
  if (typeof param === "undefined") {
    return "";
  }
  if (typeof param === "string") {
    try {
      var obj = JSON.parse(param);
      return JSON.stringify(obj);
    } catch (e) {
      console.error("The '".concat(name, "' prop is not a valid JSON string. This prop can either be a JSON string or JSON object. Please check the documentation for more details."));
    }
  } else {
    return JSON.stringify(param);
  }
  return "";
};
var parseOptionalBoolean = function (val) {
  return typeof val === "undefined" || val === null ? "" : val ? "true" : "false";
};
export { createTableFlowImporter as default };